import React from 'react';
import fhirpath from 'fhirpath';
import { FiCopy } from 'react-icons/fi';
import { Slide, toast } from 'react-toastify';

import Information from '@/components/Information';
import ToastContent from '@/components/ToastContent';
import IconButton from '@/components/IconButton';
import { FhirPedidoType } from '@/features/enterprise/models/types';
import { getSolicitacaoObrigatoriaMotivo } from '@/features/recursos/utils/solicitacaoObrigatoria';
import {
  Container,
  Header,
  HeaderTitle,
  HeaderSubTitle,
  CollapseButton,
  Actions,
} from './styles';

interface BoxExameProps {
  exame: FhirPedidoType;
  disableCopy?: boolean;
  solicitacao_obrigatoria?: boolean;
  solicitacao_obrigatoria_motivo?: string;
}

const BoxExame: React.FC<BoxExameProps> = ({
  exame,
  disableCopy,
  solicitacao_obrigatoria,
  solicitacao_obrigatoria_motivo,
}) => {
  const subtitleRef = React.createRef<HTMLHeadingElement>();
  const solicitacaoObrigatoriaMotivo = React.useMemo(() => {
    return getSolicitacaoObrigatoriaMotivo(
      'exame',
      solicitacao_obrigatoria_motivo,
    );
  }, [solicitacao_obrigatoria_motivo]);

  const [isOpenedDetails, setIsOpenedDetails] = React.useState(false);
  const [showCollapse, setShowCollape] = React.useState(false);
  const [onHover, setOnHover] = React.useState(false);

  const nome = React.useMemo(() => {
    return fhirpath.evaluate(
      exame,
      'ServiceRequest.code.text | ServiceRequest.code.coding.display',
    )[0];
  }, [exame]);

  const descricao = React.useMemo(() => {
    return fhirpath.evaluate(exame, 'ServiceRequest.note.text')[0];
  }, [exame]);

  const hasDescription = React.useMemo(() => !!descricao, [descricao]);

  const toggleDetails = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (hasDescription) {
        setIsOpenedDetails(prev => !prev);
      }
    },
    [hasDescription],
  );

  const copyExame = React.useCallback(() => {
    const texto = `${nome}`;
    navigator.clipboard.writeText(texto);

    toast.dark(
      () => (
        <ToastContent>
          <p>Exame copiado</p>
        </ToastContent>
      ),
      {
        position: 'bottom-left',
        transition: Slide,
      },
    );
  }, [nome]);

  React.useEffect(() => {
    if (subtitleRef?.current) {
      setShowCollape(
        subtitleRef?.current?.scrollHeight > subtitleRef?.current?.clientHeight,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasDescription) {
    return (
      <Container>
        <Header>
          <HeaderTitle>{nome}</HeaderTitle>
        </Header>

        {solicitacao_obrigatoria && (
          <Information type="warning" style={{ margin: '12px 0 0' }}>
            {solicitacaoObrigatoriaMotivo}
          </Information>
        )}
      </Container>
    );
  }

  return (
    <Container detailsOpen={isOpenedDetails}>
      <Header>
        <HeaderTitle highlight={isOpenedDetails || onHover}>{nome}</HeaderTitle>

        <Actions>
          {!disableCopy && (
            <IconButton
              tooltip="Copiar exame"
              onClick={copyExame}
              onMouseOver={() => setOnHover(true)}
              onMouseLeave={() => setOnHover(false)}
              icon={<FiCopy />}
            />
          )}

          {showCollapse && (
            <CollapseButton
              collapsed={isOpenedDetails}
              onClick={toggleDetails}
            />
          )}
        </Actions>
      </Header>

      <HeaderSubTitle ref={subtitleRef} highlight={isOpenedDetails}>
        {descricao}
      </HeaderSubTitle>

      {solicitacao_obrigatoria && (
        <Information type="warning" style={{ margin: '12px 0 0' }}>
          {solicitacaoObrigatoriaMotivo}
        </Information>
      )}
    </Container>
  );
};

BoxExame.defaultProps = {
  disableCopy: false,
};

export default BoxExame;
